import React, { useMemo } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { useSelector, useDispatch } from "react-redux";
import LoginForm from "./LoginForm";
import { login } from "../../actions/identity";
import identitySelectors from "../../selectors/identity";
import { AuthOrigin, useOAuth } from "../../hooks/useOAuth";
import queryString from "query-string";
import { LoginFailureMessage } from "./LoginFailureMessage";
import { Message } from "semantic-ui-react";
import { Redirect, useParams } from "react-router-dom";
import AppSettings from "../../config.js";

export const ConnectedLoginForm = ({ site, usernameLabel }: any) => {
  const query = queryString.parse(window.location.search);
  const params = useParams<{ site: string; invalid?: string }>();
  const loginForm = useForm({
    defaultValues: {
      email: "",
      password: "",
      remember: true,
    },
  });
  const email = loginForm.watch("email");
  const dispatch = useDispatch();
  const submit = ({ email, password }: any) => {
    dispatch(login({ email, password, site: params.site, employee: false }));
  };

  const { initiateOAuth, error } = useOAuth({ isEmployee: false, authOrigin: AuthOrigin.Login });

  const postLogin = useSelector(identitySelectors.postLogin);
  const postForgotPassword = useSelector(identitySelectors.postForgotPassword);
  const sentForgotPassword = !postForgotPassword.loading && postForgotPassword.payload;
  const isCitizen = useSelector(identitySelectors.isCitizen);
  const loginFailure = postLogin.error || error || query.authErr;
  const requireVerification = loginFailure && postLogin.error?.status === 403;
  const isLoginLoading = postLogin.loading ?? false;

  const message = useMemo(() => {
    if (loginFailure) return <LoginFailureMessage />;
    if (query.message) {
      // Remove the message from the URL after displaying it
      const newQuery = { ...query };
      delete newQuery.message;
      window.history.replaceState({}, "", `/${params.site}/login?${queryString.stringify(newQuery)}`);
      return (
        <Message className='feedback' positive>
          {query.message}
        </Message>
      );
    }
    if (sentForgotPassword)
      return (
        <Message className='feedback' positive>
          Please check your email for login instructions
        </Message>
      );
    return null;
  }, [sentForgotPassword, loginFailure, query.message]);

  if (requireVerification) {
    const target =
      (isCitizen ?? false) &&
      !query.redirect?.includes("public") &&
      !query.redirect?.includes(AppSettings.GOGOV_WEB_URL)
        ? null
        : query.redirect || null;
    return (
      <Redirect
        to={{
          pathname: `/${params.site}/public/verify`,
          search: queryString.stringify({
            email,
            ...(target ? { redirect: target } : {}),
          }),
        }}
      />
    );
  }

  return (
    <FormProvider {...loginForm}>
      {message}
      <LoginForm
        site={site}
        initiateOAuth={initiateOAuth}
        onSubmit={submit}
        username={{ label: usernameLabel, placeholder: "Email" }}
        isLoading={isLoginLoading}
      />
    </FormProvider>
  );
};
